import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import Spinner from "react-bootstrap/Spinner";
import { toast, ToastContainer } from "react-toastify";
import config from "../../Config";
import PayrollMenu from "./PayrollMenu";
import Pagination from "../General/Pagination";
import { scrollToTop } from "../Shared/ScrollOnTop";

const CHANGED_WRAP = "employee-history-field-changed";

function normStr(v) {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "1" : "0";
  return String(v).trim();
}

function dateKey(v) {
  if (!v || !moment(v).isValid()) return "";
  return moment(v).format("YYYY-MM-DD");
}

function genderKey(e) {
  if (!e) return "";
  return e.Gender === true || e.Gender === "true" || e.Gender === 1 ? "M" : "F";
}

function boolKey(v) {
  return v === true || v === "true" || v === 1 ? "1" : "0";
}

function numEq(a, b) {
  const na = a === null || a === undefined || a === "" ? NaN : Number(a);
  const nb = b === null || b === undefined || b === "" ? NaN : Number(b);
  if (Number.isNaN(na) && Number.isNaN(nb)) return true;
  return na === nb;
}

function employmentLabel(v) {
  if (v === "P") return "Permanent";
  if (v === "T") return "Temporary";
  return "Not Available";
}

function computeEmployeeHistoryDiff(cur, prev) {
  const out = new Set();
  if (!cur || !prev) return out;
  const add = (key, changed) => {
    if (changed) out.add(key);
  };

  add("Name", normStr(cur.Name) !== normStr(prev.Name));
  add("Father_Name", normStr(cur.Father_Name) !== normStr(prev.Father_Name));
  add("Husband_Name", normStr(cur.Husband_Name) !== normStr(prev.Husband_Name));
  add("Marital_Status", normStr(cur.Marital_Status) !== normStr(prev.Marital_Status));
  add("Gender", genderKey(cur) !== genderKey(prev));
  add("Date_Of_Birth", dateKey(cur.Date_Of_Birth) !== dateKey(prev.Date_Of_Birth));
  add("Blood_Group", normStr(cur.Blood_Group) !== normStr(prev.Blood_Group));
  add("CNIC_NO", normStr(cur.CNIC_NO) !== normStr(prev.CNIC_NO));
  add("OldSerialNo", !numEq(cur.OldSerialNo, prev.OldSerialNo));
  add("LongLeaveStatus", boolKey(cur.LongLeaveStatus) !== boolKey(prev.LongLeaveStatus));
  add("PromotionDate", dateKey(cur.PromotionDate) !== dateKey(prev.PromotionDate));
  add("EmployementStatus", normStr(cur.EmployementStatus) !== normStr(prev.EmployementStatus));

  add("Account_No", normStr(cur.Account_No) !== normStr(prev.Account_No));
  add("EOBI_NO", normStr(cur.EOBI_NO) !== normStr(prev.EOBI_NO));
  add("NTN_NO", normStr(cur.NTN_NO) !== normStr(prev.NTN_NO));
  add("Password", normStr(cur.Password) !== normStr(prev.Password));

  add("Phone_No", normStr(cur.Phone_No) !== normStr(prev.Phone_No));
  add("Mobile_No", normStr(cur.Mobile_No) !== normStr(prev.Mobile_No));
  add("Date_Of_Joining", dateKey(cur.Date_Of_Joining) !== dateKey(prev.Date_Of_Joining));
  add(
    "Designation",
    Number(cur.Selected_Designation?.ID || cur.Designation_ID || 0) !==
      Number(prev.Selected_Designation?.ID || prev.Designation_ID || 0) ||
      normStr(cur.Selected_Designation?.Name) !== normStr(prev.Selected_Designation?.Name),
  );
  add("Email_Address", normStr(cur.Email_Address) !== normStr(prev.Email_Address));
  add("Home_Address", normStr(cur.Home_Address) !== normStr(prev.Home_Address));

  add("Basic_Pay_Scale", !numEq(cur.Basic_Pay_Scale, prev.Basic_Pay_Scale));
  add("GPS_Scale", !numEq(cur.GPS_Scale, prev.GPS_Scale));
  add(
    "Campus",
    Number(cur.Employee_Campus_BE?.ID || cur.Campus_ID || 0) !==
      Number(prev.Employee_Campus_BE?.ID || prev.Campus_ID || 0) ||
      normStr(cur.Employee_Campus_BE?.Campus_Name) !== normStr(prev.Employee_Campus_BE?.Campus_Name),
  );
  add(
    "Employee_Type",
    Number(cur.Employee_Type_BE?.ID || cur.Employee_Type || 0) !==
      Number(prev.Employee_Type_BE?.ID || prev.Employee_Type || 0) ||
      normStr(cur.Employee_Type_BE?.Name) !== normStr(prev.Employee_Type_BE?.Name),
  );
  add("NoofIncrements", !numEq(cur.NoofIncrements, prev.NoofIncrements));
  add("Category", normStr(cur.Category) !== normStr(prev.Category));
  add("Qualification", normStr(cur.Qualification) !== normStr(prev.Qualification));
  add("Remarks", normStr(cur.Remarks) !== normStr(prev.Remarks));

  return out;
}

function ReadonlyField({ label, value, highlight = false }) {
  return (
    <div className={`employee-history-field employee-history-readonly ${highlight ? CHANGED_WRAP : ""}`}>
      <label>{label}</label>
      <input type="text" className="form-control" value={value ?? ""} readOnly />
    </div>
  );
}

function MetaStat({ label, value, accent = "blue" }) {
  return (
    <div className={`employee-history-meta-stat employee-history-meta-stat--${accent}`}>
      <div className="employee-history-meta-stat__label">{label}</div>
      <div className="employee-history-meta-stat__value">{value ?? "—"}</div>
    </div>
  );
}

function EmployeeHistory() {
  const params = useParams();
  const employeeId = params.id;

  const [accessKey] = useState(localStorage.getItem("AccessKey"));
  const [userId] = useState(localStorage.getItem("ID"));
  const [memberType] = useState(localStorage.getItem("MemberType"));

  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  const [previousSnapshot, setPreviousSnapshot] = useState(null);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    setPageNo(1);
  }, [employeeId]);

  const loadHistory = useCallback(async () => {
    if (!employeeId) return;

    setLoading(true);
    setPreviousSnapshot(null);

    const basePayload = {
      AccessKey: accessKey,
      UserID: userId,
      MemberType: memberType,
      pageSize,
      ID: parseInt(employeeId, 10),
    };

    try {
      const r1 = await axios({
        method: "post",
        url: config.base_url + "Payroll/GetEmployeeHistory",
        headers: { "Content-Type": "application/json" },
        data: { ...basePayload, pageNo },
      });

      const tr = r1.data.totalRecords ?? 0;
      setTotalRecords(tr);

      if (r1.data.status_code !== 1 || !r1.data.ListofRecords?.length) {
        setSnapshot(null);
        setPreviousSnapshot(null);
        return;
      }

      const current = r1.data.ListofRecords[0];
      setSnapshot(current);

      if (pageNo < tr) {
        const r2 = await axios({
          method: "post",
          url: config.base_url + "Payroll/GetEmployeeHistory",
          headers: { "Content-Type": "application/json" },
          data: { ...basePayload, pageNo: pageNo + 1 },
        });
        if (r2.data.status_code === 1 && r2.data.ListofRecords?.length > 0) {
          setPreviousSnapshot(r2.data.ListofRecords[0]);
        }
      }
    } catch {
      setSnapshot(null);
      setPreviousSnapshot(null);
      setTotalRecords(0);
      toast.error("Could not load employee history.");
    } finally {
      setLoading(false);
    }
  }, [accessKey, userId, memberType, pageNo, pageSize, employeeId]);

  useEffect(() => {
    document.title = "Employee History";
    loadHistory();
  }, [loadHistory]);

  const changedKeys = useMemo(
    () => computeEmployeeHistoryDiff(snapshot, previousSnapshot),
    [snapshot, previousSnapshot],
  );

  const dh = (...keys) => keys.some((k) => changedKeys.has(k));

  const fmt = (v) => (v && moment(v).isValid() ? moment(v).format(config.date_format_input || "YYYY-MM-DD") : "—");

  const genderLabel =
    snapshot?.Gender === true || snapshot?.Gender === "true" || snapshot?.Gender === 1 ? "Male" : "Female";

  const longLeaveLabel = snapshot?.LongLeaveStatus === true || snapshot?.LongLeaveStatus === "true" ? "Long Leave" : "Available";

  return (
    <>
      <style>{`
        .employee-history-meta-panel {
          background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 55%, #f0f9ff 100%);
          border: 1px solid #dbeafe;
          border-radius: 14px;
          padding: 1.35rem 1.5rem;
          margin-bottom: 1.5rem;
        }
        .employee-history-meta-stat {
          border-radius: 12px;
          padding: 1rem 1.1rem;
          border: 1px solid #e2e8f0;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
          height: 100%;
          background: #ffffff;
        }
        .employee-history-meta-stat--blue {
          border-left: 4px solid #3b82f6;
          background: linear-gradient(135deg, #eff6ff 0%, #ffffff 72%);
        }
        .employee-history-meta-stat--violet {
          border-left: 4px solid #8b5cf6;
          background: linear-gradient(135deg, #f5f3ff 0%, #ffffff 72%);
        }
        .employee-history-meta-stat--teal {
          border-left: 4px solid #14b8a6;
          background: linear-gradient(135deg, #f0fdfa 0%, #ffffff 72%);
        }
        .employee-history-meta-stat__label {
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          margin-bottom: 0.4rem;
          font-weight: 600;
        }
        .employee-history-meta-stat__value {
          font-size: 1.05rem;
          color: #1e293b;
          font-weight: 600;
          line-height: 1.35;
          word-break: break-word;
        }
        .employee-history-meta-hint {
          font-size: 0.875rem;
          color: #64748b;
          margin-bottom: 0.35rem;
        }
        .employee-history-meta-hint--changed {
          display: inline-block;
          margin-top: 0.25rem;
          padding: 0.45rem 0.85rem;
          border-radius: 999px;
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
          color: #047857;
          font-weight: 500;
        }
        .employee-history-field {
          margin-bottom: 1rem;
        }
        .employee-history-readonly label {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 0.35rem;
        }
        .employee-history-readonly .form-control {
          background-color: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          color: #334155;
          cursor: default;
        }
        .employee-history-readonly .form-control:focus {
          box-shadow: none;
          border-color: #e2e8f0;
        }
        .${CHANGED_WRAP} label {
          color: #0f766e;
          font-weight: 600;
        }
        .${CHANGED_WRAP} label::after {
          content: " · changed";
          font-size: 0.72rem;
          color: #14b8a6;
          font-weight: 500;
        }
        .${CHANGED_WRAP} .form-control {
          border-color: #5eead4 !important;
          background: linear-gradient(90deg, #f0fdfa 0%, #ffffff 100%) !important;
          box-shadow: inset 3px 0 0 #14b8a6, 0 0 0 3px rgba(20, 184, 166, 0.1) !important;
          color: #134e4a !important;
        }
      `}</style>
      <PayrollMenu />
      <ToastContainer />
      <div className="page-head">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-8">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/Dashboard" onClick={scrollToTop}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/PayRoll/PayRollDashboard">Pay Roll</Link>
                  </li>
                  <li className="breadcrumb-item active">Employee history</li>
                </ol>
              </nav>
              <div className="page-title">Employee record history</div>
            </div>
            <div className="col-md-4 text-md-end">
              {employeeId ? (
                <Link to={`/Payroll/EditEmployee/${employeeId}`} className="button button-white">
                  Edit employee
                </Link>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status" />
            </div>
          ) : !snapshot ? (
            <div className="card">
              <div className="card-body text-center text-muted py-5">
                No history records were found for this employee.
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                <div className="employee-history-meta-panel">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <MetaStat label="History record #" value={snapshot.ID} accent="blue" />
                    </div>
                    <div className="col-md-4">
                      <MetaStat
                        label="Saved at"
                        value={moment(snapshot.Updated_Date).format("DD/MM/YYYY, h:mm:ss a")}
                        accent="violet"
                      />
                    </div>
                    <div className="col-md-4">
                      <MetaStat label="Employee code" value={snapshot.Code} accent="teal" />
                    </div>
                    <div className="col-md-12">
                      <p className="employee-history-meta-hint mb-1">
                        Snapshot {pageNo} of {totalRecords} (newest first). Use pagination below to browse older revisions.
                      </p>
                      {previousSnapshot ? (
                        <p className="employee-history-meta-hint employee-history-meta-hint--changed mb-0">
                          Teal-highlighted fields differ from the previous (older) revision.
                        </p>
                      ) : (
                        <p className="employee-history-meta-hint mb-0">
                          This is the oldest stored revision; there is no earlier snapshot to compare.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-wrap">
                  <div className="form-legend" style={{ left: "64px" }}>Basic Information</div>
                  <div className="row align-items-center">
                    <div className="col-md-9">
                      <div className="row">
                        <div className="col-md-3">
                          <ReadonlyField label="Employee code" value={snapshot.Code} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField label="Employee name" value={snapshot.Name} highlight={dh("Name")} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField label="Father name" value={snapshot.Father_Name} highlight={dh("Father_Name")} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField label="Husband name" value={snapshot.Husband_Name} highlight={dh("Husband_Name")} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField label="Marital status" value={snapshot.Marital_Status} highlight={dh("Marital_Status")} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField label="Gender" value={genderLabel} highlight={dh("Gender")} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField label="Date of birth" value={fmt(snapshot.Date_Of_Birth)} highlight={dh("Date_Of_Birth")} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField label="Blood group" value={snapshot.Blood_Group} highlight={dh("Blood_Group")} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField label="CNIC no." value={snapshot.CNIC_NO} highlight={dh("CNIC_NO")} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField label="Old serial no." value={snapshot.OldSerialNo} highlight={dh("OldSerialNo")} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField label="Long leave status" value={longLeaveLabel} highlight={dh("LongLeaveStatus")} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField label="Promotion date" value={fmt(snapshot.PromotionDate)} highlight={dh("PromotionDate")} />
                        </div>
                        <div className="col-md-3">
                          <ReadonlyField
                            label="Employment status"
                            value={employmentLabel(snapshot.EmployementStatus)}
                            highlight={dh("EmployementStatus")}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 text-center">
                      <img
                        className="mb-3"
                        src={config.StaffImagePath + (snapshot.Code || "") + ".jpg"}
                        alt=""
                        style={{ width: "150px" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-wrap">
                  <div className="form-legend" style={{ left: "64px" }}>Account Information</div>
                  <div className="row">
                    <div className="col-md-3">
                      <ReadonlyField label="Bank account no." value={snapshot.Account_No} highlight={dh("Account_No")} />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField label="EOBI no." value={snapshot.EOBI_NO} highlight={dh("EOBI_NO")} />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField label="NTN no." value={snapshot.NTN_NO} highlight={dh("NTN_NO")} />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField
                        label="Password"
                        value={snapshot.Password ? "•••••••• (stored)" : "—"}
                        highlight={dh("Password")}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-wrap">
                  <div className="form-legend" style={{ left: "64px" }}>Contact Information</div>
                  <div className="row">
                    <div className="col-md-3">
                      <ReadonlyField label="Phone no." value={snapshot.Phone_No} highlight={dh("Phone_No")} />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField label="Mobile no." value={snapshot.Mobile_No} highlight={dh("Mobile_No")} />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField label="Date of joining" value={fmt(snapshot.Date_Of_Joining)} highlight={dh("Date_Of_Joining")} />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField
                        label="Designation"
                        value={snapshot.Selected_Designation?.Name || "—"}
                        highlight={dh("Designation")}
                      />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField label="Email address" value={snapshot.Email_Address} highlight={dh("Email_Address")} />
                    </div>
                    <div className="col-md-9">
                      <ReadonlyField label="Home address" value={snapshot.Home_Address} highlight={dh("Home_Address")} />
                    </div>
                  </div>
                </div>

                <div className="form-wrap">
                  <div className="form-legend" style={{ left: "64px" }}>Pay / Qualification Information</div>
                  <div className="row">
                    <div className="col-md-3">
                      <ReadonlyField label="Basic pay scale" value={snapshot.Basic_Pay_Scale} highlight={dh("Basic_Pay_Scale")} />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField label="Pay scale GPS" value={snapshot.GPS_Scale} highlight={dh("GPS_Scale")} />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField
                        label="Campus"
                        value={snapshot.Employee_Campus_BE?.Campus_Name || "—"}
                        highlight={dh("Campus")}
                      />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField
                        label="Group / department"
                        value={snapshot.Employee_Type_BE?.Name || "—"}
                        highlight={dh("Employee_Type")}
                      />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField label="No. of increments" value={snapshot.NoofIncrements} highlight={dh("NoofIncrements")} />
                    </div>
                    <div className="col-md-3">
                      <ReadonlyField label="Category" value={snapshot.Category || "—"} highlight={dh("Category")} />
                    </div>
                    <div className="col-md-12">
                      <ReadonlyField label="Qualification" value={snapshot.Qualification} highlight={dh("Qualification")} />
                    </div>
                    <div className="col-md-12">
                      <ReadonlyField label="Remarks" value={snapshot.Remarks} highlight={dh("Remarks")} />
                    </div>
                  </div>
                </div>

                {totalRecords > 0 ? (
                  <div className="row mt-3">
                    <div className="col-12">
                      <Pagination
                        pageNo={pageNo}
                        pageSize={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={setPageNo}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default EmployeeHistory;
